// PRD 07 §3.3 / §8.2 — destructive confirm before delete.
// Message warns that PRO Item Design references will be auto-unlinked. On
// success, the BE-reported `unlinkedPidCount` informs the toast copy so users
// see the actual fan-out (matches the spirit of PRD §3.3 even though the
// canonical toast in the PRD is the simple "Management guideline deleted.").

import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import { useDeleteGuideline } from '../hooks/use-guideline-mutations';
import type { ManagementGuideline } from '../types/management-guidelines.types';
import { getGuidelineErrorMessage } from '../utils/error-message';

interface DeleteGuidelineConfirmProps {
  guideline: ManagementGuideline | null;
  onClose: () => void;
}

export function DeleteGuidelineConfirm({ guideline, onClose }: DeleteGuidelineConfirmProps) {
  const { t } = useTranslation('management-guidelines');
  const deleteMutation = useDeleteGuideline();

  const handleConfirm = async () => {
    if (!guideline || deleteMutation.isPending) return;
    try {
      const { unlinkedPidCount } = await deleteMutation.mutateAsync(guideline.gid);
      toast.success(
        unlinkedPidCount > 0
          ? t('toasts.deletedWithUnlink', { count: unlinkedPidCount })
          : t('toasts.deleted'),
      );
      onClose();
    } catch (e) {
      toast.error(
        getGuidelineErrorMessage(e, t, 'toasts.deleteError'),
      );
    }
  };

  return (
    <ConfirmModal
      open={guideline !== null}
      title={t('deleteConfirm.title')}
      message={
        guideline
          ? t('deleteConfirm.message', { name: guideline.name })
          : ''
      }
      confirmLabel={t('deleteConfirm.confirm')}
      cancelLabel={t('deleteConfirm.cancel')}
      confirmVariant="destructive"
      loading={deleteMutation.isPending}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (deleteMutation.isPending) return;
        onClose();
      }}
    />
  );
}
